import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import ChildCard from './ChildCard';
import EffectsLayer from './EffectsLayer';
import FinishOverlay from './FinishOverlay';
import TimerCard from './TimerCard';
import TopBar from './TopBar';
import useAudioEffects from './effects/useAudioEffects';
import useDepartureWatcher from './effects/useDepartureWatcher';
import useTicker from './effects/useTicker';
import useVisualEffects from './effects/useVisualEffects';
import {
  buildTaskFeedback,
  clone,
  formatTime,
  getTaskPointBreakdown,
  mergePointStats,
  nextDeparture,
  createRuntimeChildren,
} from './utils';
import { useLocalStorage } from './useLocalStorage';

const RoutinePage = ({
  findRoutineById,
  activeRoutine,
  openSettings,
}) => {
  const navigate = useNavigate();

  const [streak, setStreak] = useLocalStorage('morningMissionStreak', 0); 
  const [lastDayKey, setLastDayKey] = useLocalStorage('morningMissionLastDay', '');

  const { routineId } = useParams();
  const targetRoutine = routineId ? findRoutineById(routineId) : null;
  // const targetRoutineId = targetRoutine?.id || '';
  const routine = targetRoutine || activeRoutine;

  const [finished, setFinished] = useState(false);
  const [taskWinners, setTaskWinners] = useState({});
  const [taskFirstTimes, setTaskFirstTimes] = useState({});
  const [participants, setParticipants] = useState(() =>
    createRuntimeChildren(routine.children).map((child) => ({ ...child, index: 0, taskStart: Date.now() })),
  );

  const [deadlineTs, setDeadlineTs] = useState(() => nextDeparture(routine.hour, routine.minute).getTime());
  const tick = useTicker();
  const { sfxPop, sfxLose, sfxWin, sfxEnd } = useAudioEffects();
  const { flash, showFeedback, confettiBurst, rippleFromEvent, flashType, flashOn, feedback, feedbackVisible, confetti, ripple } =
    useVisualEffects();

  const [winChild, setWinChild] = useState(null);
  const [shakeChild, setShakeChild] = useState(null);
  const [finishReason, setFinishReason] = useState('completed');

  // Play start sound on mount
  useEffect(() => { sfxPop(); }, []); // eslint-disable-line
  
  const departure = useMemo(() => {
    if (deadlineTs) return new Date(deadlineTs);
    return nextDeparture(routine.hour, routine.minute);
  }, [routine.hour, routine.minute, deadlineTs, tick]);
  const diff = departure - new Date();
  const urgent = diff <= 5 * 60 * 1000;

  useEffect(() => {
    setDeadlineTs(nextDeparture(routine.hour, routine.minute).getTime());
    setParticipants(createRuntimeChildren(routine.children).map((child) => ({ ...child, index: 0, taskStart: Date.now() })));
  }, [routine]);
 
  const updateStreak = (dayKey, completed) => {
    if (!completed) return;
    if (lastDayKey !== dayKey) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yKey = yesterday.toISOString().slice(0, 10);
      const next = lastDayKey === yKey ? streak + 1 : 1;
      setStreak(next);
      setLastDayKey(dayKey);
    }
  };


  const allDone = (arr) => arr.every((c) => c.index >= routine.tasks.length);

  const showTaskResult = (childIndex, childName, first, comeback, event) => {
    setWinChild(childIndex);
    setShakeChild(first ? null : childIndex === 0 ? 1 : 0);
    setTimeout(() => {
      setWinChild(null);
      setShakeChild(null);
    }, 450);
    if (first) { flash('yellow'); confettiBurst(); sfxWin(); }
    else if (comeback) { flash('yellow'); sfxWin(); }
    else { flash(childIndex === 0 ? 'pink' : 'green'); sfxLose(); }
    showFeedback(buildTaskFeedback({ childName, first, comeback }));
    rippleFromEvent(event);
  };

  const completeTask = (childIndex, event) => {
    if (finished) return;
    setParticipants((prev) => {
      const next = clone(prev);
      const child = next[childIndex];
      if (child.index < 0 || child.index >= routine.tasks.length) return prev;

      const taskIndex = child.index;
      const task = routine.tasks[taskIndex];
      const elapsed = child.taskStart ? Date.now() - child.taskStart : 0;
      const first = taskWinners[taskIndex] === undefined;
      const firstElapsed = taskFirstTimes[taskIndex];
      const comeback = !first && typeof firstElapsed === 'number' && elapsed < firstElapsed;
      const pointBreakdown = getTaskPointBreakdown({ first, comeback, elapsedMs: elapsed });

      child.points += pointBreakdown.total;
      child.pointStats = mergePointStats(child.pointStats, pointBreakdown);
      child.done.unshift({
        icon: task.icon,
        title: task.title,
        time: formatTime(elapsed),
        note: first ? '🏅 Først færdig' : comeback ? '⚡ Comeback-bonus' : '✨ Flot fuldført',
        pointsAwarded: pointBreakdown.total,
      });
      child.lastTaskMs = elapsed;
      child.index += 1;
      child.taskStart = child.index < routine.tasks.length ? Date.now() : null;

      if (first) {
        setTaskWinners((w) => ({ ...w, [taskIndex]: childIndex }));
        setTaskFirstTimes((t) => ({ ...t, [taskIndex]: elapsed }));
        child.wins += 1;
      }

      showTaskResult(childIndex, child.name, first, comeback, event);

      if (allDone(next)) {
        setFinishReason('completed');
        const dayKey = new Date().toISOString().slice(0, 10);
        updateStreak(dayKey, true);
        setTimeout(() => setFinished(true), 1000);
      }

      return next;
    });
  };

  const handleTimeUp = useCallback(() => {
    setFinishReason('timeout');
    sfxEnd();
    flash('yellow');
    setTimeout(() => setFinished(true), 2000);
  }, [flash, setFinished, setFinishReason, sfxEnd]);

  useDepartureWatcher({ finished, tick, deadlineTs, onTimeUp: handleTimeUp });

  const openRoutineSettings = useCallback(() => {
    openSettings(`/routine/${routine.id}`);
  }, [openSettings, routine.id]);

  const resetAll = useCallback(() => {
    navigate('/');
  }, [navigate, routine.children]);

  if (!targetRoutine) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <EffectsLayer
        flashType={flashType}
        flashOn={flashOn}
        confetti={confetti}
        ripple={ripple}
        feedback={feedback}
        feedbackVisible={feedbackVisible}
      />
      <div className="app">
        <TopBar routine={routine} title={routine.title} onOpenSettings={openRoutineSettings} onReset={resetAll} />
        <TimerCard hour={routine.hour} minute={routine.minute} diff={diff} urgent={urgent} />
        <div className="split">
          {participants.map((child, index) => {
            const isDone = child.index >= routine.tasks.length;
            const currentTask = !isDone ? routine.tasks[child.index] : null;
            const elapsed = currentTask && child.taskStart ? formatTime(Date.now() - child.taskStart) : '0m 00s';
            const progress = routine.tasks.length
              ? Math.max(0, Math.min(100, (Math.max(0, child.index) / routine.tasks.length) * 100))
              : 0;
            const currentWinner = taskWinners[child.index] === index;
            return (
              <ChildCard
                key={child.name}
                child={child}
                index={index}
                currentTask={currentTask}
                elapsed={elapsed}
                progress={progress}
                currentWinner={currentWinner}
                isDone={isDone}
                isWin={winChild === index}
                isShake={shakeChild === index}
                onComplete={(e) => completeTask(index, e)}
              />
            );
          })}
        </div>
      </div>
      {finished && (
        <FinishOverlay
          finishReason={finishReason}
          participants={participants}
          activeRoutine={routine}
          resetAll={resetAll}
        />
      )}
    </>
  );
};

export default RoutinePage;
